import { createHomepageCatalog } from './sts-homepage-catalog.js?v=replays-20261018-1';

const initialized = new WeakMap();
const watchedDocuments = new WeakMap();
const dateFormat = new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' });

export function initializeReplayFeed(root, { catalog = createHomepageCatalog({ publicKey: root.dataset.publicKey }), schedule = setTimeout, cancel = clearTimeout } = {}) {
  if (initialized.has(root)) return initialized.get(root);
  const doc = root.ownerDocument, view = doc.defaultView;
  const grid = root.querySelector('[data-replay-grid]'), status = root.querySelector('[data-replay-status]'), retry = root.querySelector('[data-replay-retry]');
  let timer, timeout, pending, request, destroyed = false, queued = false, key = '', loaded = false;
  function render(rows) {
    const nextKey = JSON.stringify(rows);
    if (key !== nextKey) {
      key = nextKey;
      const cards = rows.map(show => {
        const card = doc.createElement('article'); card.className = 'sts-native-replay-card';
        const picture = doc.createElement('div'); picture.className = 'sts-native-replay-image';
        if (show.bannerImageUrl) {
          const image = doc.createElement('img'); image.src = show.bannerImageUrl; image.alt = ''; image.width = 1120; image.height = 900; image.loading = 'lazy';
          image.addEventListener('error', () => image.remove(), { once: true }); picture.append(image);
        }
        const badge = doc.createElement('span'); badge.textContent = 'Replay';
        const play = doc.createElement('span'); play.textContent = '▶'; play.setAttribute('aria-hidden', 'true'); picture.append(badge, play);
        const copy = doc.createElement('div'); copy.className = 'sts-native-replay-copy';
        const date = doc.createElement('time'); date.className = 'sts-native-note'; date.dateTime = show.startTime; date.textContent = dateFormat.format(new Date(show.startTime));
        const title = doc.createElement('h3'); title.textContent = show.title;
        const link = doc.createElement('a'); link.className = 'sts-native-link'; link.textContent = 'Watch the replay →'; link.setAttribute('aria-label', `Watch the replay: ${show.title}`);
        const current = new URL(view.location.href), href = new URL(`${root.dataset.storeRoot ?? '/'}pages/streams`, current.origin);
        href.searchParams.set('view', 'sts-live-native'); href.searchParams.set('showId', show.id); href.hash = 'sts-native-watch';
        const preview = current.searchParams.get('preview_theme_id'); if (/^\d+$/.test(preview ?? '')) href.searchParams.set('preview_theme_id', preview);
        link.href = href.href; copy.append(date, title, link); card.append(picture, copy); return card;
      });
      grid.replaceChildren(...cards);
    }
    loaded = true; root.dataset.replayState = rows.length ? 'ready' : 'empty';
    status.textContent = rows.length ? '' : 'No replays are ready yet. Check back for our next show.'; status.hidden = rows.length > 0; retry.hidden = true;
  }
  function refresh() {
    if (destroyed) return Promise.resolve();
    if (pending) return pending;
    cancel(timer); request = new view.AbortController(); const signal = request.signal; let timedOut = false;
    timeout = schedule(() => { timedOut = true; request.abort(); }, 8000);
    pending = (async () => {
      try {
        const rows = await catalog.readReplays({ limit: Math.max(1, Math.min(6, Number(root.dataset.replayCount) || 3)), signal });
        if (!destroyed && !signal.aborted) render(rows);
        else if (timedOut) throw Error('Recording request timed out');
      } catch {
        if (!destroyed && (!signal.aborted || timedOut)) {
          root.dataset.replayState = 'error'; status.hidden = false; retry.hidden = false;
          status.textContent = loaded ? 'We cannot refresh recordings right now. Your recent replays are kept here.' : 'We cannot load recordings right now. Please try again.';
        }
      } finally {
        cancel(timeout); pending = undefined;
        if (!destroyed && doc.visibilityState !== 'hidden') {
          if (queued) { queued = false; void refresh(); } else timer = schedule(refresh, 60000);
        }
      }
    })();
    return pending;
  }
  function foreground() {
    if (doc.visibilityState === 'hidden') { cancel(timer); request?.abort(); }
    else if (pending) queued = true;
    else void refresh();
  }
  retry.addEventListener('click', refresh); doc.addEventListener('visibilitychange', foreground);
  const manager = { refresh, destroy() {
    if (destroyed) return; destroyed = true; cancel(timer); cancel(timeout); request?.abort();
    retry.removeEventListener('click', refresh); doc.removeEventListener('visibilitychange', foreground); initialized.delete(root);
  } };
  initialized.set(root, manager); void refresh(); return manager;
}

export function observeReplayFeeds(doc, dependencies = {}) {
  if (watchedDocuments.has(doc)) return watchedDocuments.get(doc);
  const roots = new Map(), view = doc.defaultView;
  function reconcile() {
    for (const [root, manager] of roots) if (!root.isConnected) { manager.destroy(); roots.delete(root); }
    for (const root of doc.querySelectorAll('[data-sts-replay-feed]')) if (!roots.has(root)) roots.set(root, initializeReplayFeed(root, dependencies));
  }
  const observer = new view.MutationObserver(reconcile);
  function resume() { observer.observe(doc.documentElement, { childList: true, subtree: true }); reconcile(); }
  function suspend() { observer.disconnect(); for (const manager of roots.values()) manager.destroy(); roots.clear(); }
  const watcher = { disconnect() { suspend(); view.removeEventListener('pagehide', suspend); view.removeEventListener('pageshow', resume); watchedDocuments.delete(doc); } };
  watchedDocuments.set(doc, watcher); view.addEventListener('pagehide', suspend); view.addEventListener('pageshow', resume); resume(); return watcher;
}

if (typeof document !== 'undefined') observeReplayFeeds(document);
